/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ContentType, Entitlement, VodOttActionDetailsList } from 'client-code/action-details';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { Constants } from '../../../core/constants';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import { TOverlayOptions } from '../../../core/types';
import { VodPlayAction } from '../../../core/usecases/VodPlayAction';
import { DateTime } from '../../../core/utils';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { OverlayTypes } from '../OverlayTypes';

const TAG = 'VodFormatSelectorOverlay';

const generateOverlaySubtitle = (actionDetails): string => {
    // Check if any action in watchActionsList has isAdult: true
    const hasAdultContent =
        actionDetails.watchActionsList?.some((action) => action.kernel?.isAdult === true) &&
        new ServicesClientImpl().isPCHideAdultContentEnabled();

    if (hasAdultContent) {
        return Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
    }

    const { contentType, title, movieYear, seasonNumber, episodeNumber, episodeTitle } =
        actionDetails;

    let subtitle = title || '';

    switch (contentType) {
        case ContentType.Movie:
            if (movieYear) {
                subtitle += ` (${movieYear})`;
            }
            break;

        case ContentType.Episode: {
            // Check if we have any season/episode info to show
            const hasSeasonInfo = seasonNumber && seasonNumber > 0;
            const hasEpisodeInfo = episodeNumber && episodeNumber > 0;

            if (hasSeasonInfo || hasEpisodeInfo) {
                const seasonEpisodeText = AppUtils.generateSeasonEpisodeText(
                    hasSeasonInfo ? seasonNumber : 0,
                    hasEpisodeInfo ? episodeNumber : 0,
                );
                subtitle += ` - ${seasonEpisodeText}`;
            }

            if (episodeTitle) {
                subtitle += ` ${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`;
            } else {
                subtitle += ` ${LEFT_QUOTE}${Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)}${RIGHT_QUOTE}`;
            }
            break;
        }

        default:
            // For any other content type, just use the title
            break;
    }

    return subtitle;
};

const getVideoQualityPriority = (action: any): number => {
    if (action.is3D) return 1;

    const resolution = action.videoResolution?.toLowerCase();
    if (resolution?.includes('uhd')) return 2;
    if (resolution?.includes('hd')) {
        return 3;
    }
    return 4;
};

const getVodTypePriority = (action: any): number => {
    const { entitlement } = action.kernel;

    // FVOD
    if (entitlement === Entitlement.FreeNoPurchase) {
        return 1;
    }

    // Entitled SVOD
    if (entitlement === Entitlement.Subscribed) {
        return 2;
    }

    // Entitled TVOD
    if (entitlement === Entitlement.Rented) {
        return 3;
    }

    // ZVOD
    if (entitlement === Entitlement.FreeZeroPurchasable) {
        return 4;
    }

    // Unentitled SVOD
    if (entitlement === Entitlement.Subscribable) {
        return 5;
    }

    // Unentitled TVOD
    if (entitlement === Entitlement.Rentable || entitlement === Entitlement.Purchasable) {
        return 6;
    }

    // Default fallback or unknown entitlement
    return 7;
};

const sortWatchActions = (watchActionsList: any[]): any[] => {
    return [...watchActionsList].sort((a, b) => {
        // Primary sort: Video quality (3D > UHD > HD > SD)
        const qualityDiff = getVideoQualityPriority(a) - getVideoQualityPriority(b);
        if (qualityDiff !== 0) {
            return qualityDiff;
        }

        // Secondary sort: VOD type priority within same quality
        return getVodTypePriority(a) - getVodTypePriority(b);
    });
};

const hasChannelColumn = (watchActionsList: any[]): boolean => {
    return watchActionsList.some((action) => {
        const { channel } = action as any;
        return channel?.callSign;
    });
};

const hasAirDateTimeColumn = (watchActionsList: any[]): boolean => {
    return watchActionsList.some((action) => action.startTime);
};

/**
 * Calculates the maximum column count based on available data
 * Based on calculateMaxColumnCount logic from reference implementation
 */
const calculateMaxColumnCount = (watchActionsList: any[]): number => {
    const hasChannel = hasChannelColumn(watchActionsList);
    const hasStartTime = hasAirDateTimeColumn(watchActionsList);

    let maxColumn = 3; // Default: Format | Gap | Entitlement

    if (hasChannel && hasStartTime) {
        maxColumn = 4; // Format | Channel | StartTime | Entitlement
    }
    // All other cases (neither, or only one) remain at maxColumn = 3
    return maxColumn;
};

const getFormatDisplayText = (action: any): string => {
    if (action.is3D) return '3D';

    const resolution = action.videoResolution?.toLowerCase();
    if (resolution?.includes('uhd')) return 'UHD';
    if (resolution?.includes('hd')) {
        return 'HD';
    }
    return 'SD';
};

const getEntitlementStatusMessage = (action: any): string => {
    const { entitlement, formattedPrice, rentalDuration } = action.kernel;

    let entitlementText = '';
    if (entitlement != null) {
        if (entitlement === Entitlement.Rented) {
            entitlementText = Locale.getString(LocaleKeys.VOD_FORMAT_SELECTOR_RENTED);
        } else if (
            entitlement === Entitlement.FreeZeroPurchasable ||
            entitlement === Entitlement.FreeNoPurchase
        ) {
            entitlementText = Locale.getString(LocaleKeys.VOD_FORMAT_SELECTOR_FREE);
        } else if (entitlement === Entitlement.Subscribed) {
            entitlementText = Locale.getString(
                LocaleKeys.VOD_FORMAT_SELECTOR_INCLUDED_WITH_SUBSCRIPTION,
            );
        } else if (entitlement === Entitlement.Subscribable) {
            entitlementText = Locale.getString(LocaleKeys.VOD_FORMAT_SELECTOR_UPGRADE_TO_WATCH);
        } else if (
            entitlement === Entitlement.Purchasable ||
            entitlement === Entitlement.Rentable
        ) {
            if (Constants.VOD_PURCHASE_NOT_AVAILABLE) {
                entitlementText = Locale.getString(
                    LocaleKeys.VOD_FORMAT_SELECTOR_NOT_AVAILABLE_FOR_PURCHASE,
                );
            } else if (rentalDuration > 0 && formattedPrice !== '') {
                const durationHours = Math.floor(rentalDuration / 3600);
                const durationText = `${durationHours} hour${durationHours !== 1 ? 's' : ''}`;

                entitlementText = Locale.getString(LocaleKeys.VOD_FORMAT_SELECTOR_WITH_DURATION)
                    .replace('PRICE', formattedPrice)
                    .replace('DURATION', durationText);
            } else if (rentalDuration === 0 && formattedPrice !== '') {
                entitlementText = formattedPrice;
            }
        }
    }

    return entitlementText;
};

const getVodAvailabilityMessage = (action: any): string => {
    const { entitlement, rentalExpirationTime, availableEndTime } = action.kernel;
    let expirationTime = 0;

    if (rentalExpirationTime) {
        expirationTime = new Date(rentalExpirationTime).getTime();
    } else if (availableEndTime) {
        expirationTime = new Date(availableEndTime).getTime();
    }

    if (!expirationTime) {
        return '';
    }

    const currentTime = new Date().getTime();
    const expirationDiffInMSec = expirationTime - currentTime;
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

    if (expirationDiffInMSec > 0 && expirationDiffInMSec < thirtyDaysInMs) {
        const expirationDate = new Date(expirationTime);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        if (entitlement === Entitlement.Rented) {
            let templateKey = '';

            if (expirationDate.toDateString() === today.toDateString()) {
                templateKey = LocaleKeys.VOD_FORMAT_SELECTOR_RENTAL_AVAILABILITY_TODAY;
            } else if (expirationDate.toDateString() === tomorrow.toDateString()) {
                templateKey = LocaleKeys.VOD_FORMAT_SELECTOR_RENTAL_AVAILABILITY_TOMORROW;
            } else {
                templateKey = LocaleKeys.VOD_FORMAT_SELECTOR_RENTAL_AVAILABILITY_DATE;
            }

            const templateString = Locale.getString(templateKey);
            const timeStr = new DateTime(expirationTime).strftime('%l:%M%p');
            const expirationDateString = new DateTime(expirationTime).strftime('%M/%D');
            return templateString.replace('DATE', expirationDateString).replace('TIME', timeStr);
        }

        if (entitlement === Entitlement.Rentable || entitlement === Entitlement.Purchasable) {
            const { VOD_PURCHASE_NOT_AVAILABLE } = Constants;

            if (VOD_PURCHASE_NOT_AVAILABLE) {
                return '';
            }
        }

        let templateKey = '';

        if (expirationDate.toDateString() === today.toDateString()) {
            templateKey = LocaleKeys.VOD_FORMAT_SELECTOR_AVAILABILITY_TODAY;
        } else if (expirationDate.toDateString() === tomorrow.toDateString()) {
            templateKey = LocaleKeys.VOD_FORMAT_SELECTOR_AVAILABILITY_TOMORROW;
        } else {
            templateKey = LocaleKeys.VOD_FORMAT_SELECTOR_AVAILABILITY_DATE;
        }

        const templateString = Locale.getString(templateKey);
        const timeStr = new DateTime(expirationTime).strftime('%l:%M%p');
        const expirationDateString = new DateTime(expirationTime).strftime('%M/%D');
        return templateString.replace('DATE', expirationDateString).replace('TIME', timeStr);
    }

    return '';
};

const getEntitlementDisplayText = (action: any): string => {
    const { entitlement } = action.kernel;

    if (entitlement === Entitlement.Rented) {
        return getVodAvailabilityMessage(action);
    }

    return getEntitlementStatusMessage(action);
};

/**
 * Formats button text for VOD format selector options according to requirements
 */
const getFormattedTextForFormatButtons = (action: any, maxColumn: number): string => {
    const buttonTextParts: string[] = [];

    // Column 1: Video format (3D, UHD, HD, SD) - always present
    buttonTextParts.push(getFormatDisplayText(action));

    if (maxColumn === 3) {
        // Simple 3-column layout: Format | callSign/airingTime/Gap | Entitlement
        const channelCallsign = action.kernel?.channel?.callSign;
        const airingTime = action.startTime;

        if (channelCallsign) {
            buttonTextParts.push(channelCallsign);
        } else if (airingTime) {
            const formattedTime = new DateTime(airingTime).strftime('%a %M/%D %l:%i%p');
            buttonTextParts.push(formattedTime);
        } else {
            buttonTextParts.push(' '); // Placeholder for gap column
        }
    } else if (maxColumn === 4) {
        // 4-column layout: Format | callSign | airingTime | Entitlement
        const channelCallsign = action.kernel?.channel?.callSign;
        const airingTime = action.startTime;

        if (channelCallsign) {
            buttonTextParts.push(channelCallsign);
        } else {
            buttonTextParts.push(' '); // Placeholder for gap column
        }
        if (airingTime) {
            const formattedTime = new DateTime(airingTime).strftime('%a %M/%D %l:%i%p');
            buttonTextParts.push(formattedTime);
        } else {
            buttonTextParts.push(' '); // Placeholder for gap column
        }
    }

    // Always push entitlement as final column
    buttonTextParts.push(getEntitlementDisplayText(action));

    return buttonTextParts.join('#');
};

export const showVodFormatSelectorOverlay = () => (dispatch: Function) => {
    try {
        const { actionDetails } = PlaybackDataModelImpl.getInstance().getActionDetailsList();
        const { watchActionsList } = actionDetails as VodOttActionDetailsList;

        if (!watchActionsList || watchActionsList.length === 0) {
            Log.e(TAG, 'No watch actions available for format selector');
            return;
        }

        const sortedActions = sortWatchActions(watchActionsList);

        const maxColumn = calculateMaxColumnCount(sortedActions);

        const overlayOptions: TOverlayOptions[] = sortedActions.map((action): TOverlayOptions => {
            return {
                text: getFormattedTextForFormatButtons(action, maxColumn),
                value: { action, maxColumn },
            };
        });

        const overlayTitle = actionDetails.watchActionsList?.[0]?.providerDisplayName || '';

        const subtitle = generateOverlaySubtitle(actionDetails);

        const overlayProps: IOverlayDialog = {
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: VodPlayAction.GLOBAL_MOUSE_TRAP_FORMAT_SELECTOR_OVERLAY,
            options: overlayOptions,
            subtitle,
            title: overlayTitle,
            overlayType: OverlayTypes.VOD_FORMAT_SELECTOR,
            isLargeSizeOverlay: true,
        };

        dispatch(hideOverlayDialog());
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY));
        dispatch(showOverlayDialog(overlayProps));

        Log.d(TAG, 'VOD Format Selector overlay shown with', sortedActions.length, 'options');
    } catch (error) {
        Log.e(TAG, 'Error showing VOD format selector overlay:', error);
    }
};

export const onKeyBackVodFormatSelectorOverlay = () => (dispatch: Function) => {
    dispatch(hideOverlayDialog());
};
